import { AdminApiError, type AdminClient, type PlayerView } from '@apx/admin-sdk';
import { askConfirm, customModal, html } from '../modal.js';
import { fmtDate, fmtMoney } from '../format.js';
import { toDecimalString, toMajorNumber } from '@apx/money';
import { moneyUnitHint, readMoney } from '../money-input.js';
import { emptyState } from '../empty-state.js';
import { exportXlsx } from '../xlsx.js';
import { applySort, getSortState, sortableTh, wireSortHeaders } from '../sort.js';
import { skeletonTable } from '../skeleton.js';
import { showToast } from '../toast.js';
import { icon } from '../icons.js';

export interface PlayersPageDeps {
  client: AdminClient;
  integratorId: string;
}

type StatusFilter = '' | 'active' | 'banned';
type IdentityFilter = '' | 'named' | 'anon';

const PAGE_SIZE = 25;

export async function renderPlayersPage(root: HTMLElement, deps: PlayersPageDeps): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Player list</h1>
    <p class="page-sub">Search + filter players. Adjust balances, toggle anonymity, or ban inline — mutations need <code>PLAYER_BAN</code> / <code>PLAYER_BALANCE_ADJUST</code> and are audit-logged. Click a row for the full player detail.</p>

    <div id="players-stats" class="players-stats"></div>

    <div class="card">
      <div class="row players-filters">
        <input id="player-search" type="text" placeholder="Search id, integrator ref, or username…" class="players-search-input" />
        <button id="player-search-run" class="primary">Search</button>
        <select id="players-f-status" class="players-filter-select" title="Status">
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="banned">Banned</option>
        </select>
        <select id="players-f-identity" class="players-filter-select" title="Identity">
          <option value="">Any identity</option>
          <option value="named">Named</option>
          <option value="anon">Anonymous</option>
        </select>
        <button id="player-search-clear" class="ghost">Clear</button>
        <button id="player-export" class="ghost">Export page CSV</button>
      </div>
    </div>

    <div class="card">
      <div id="players-table">${skeletonTable({ rows: PAGE_SIZE, columns: 7 })}</div>
    </div>
  `;

  const tableEl = root.querySelector<HTMLElement>('#players-table')!;
  const statsEl = root.querySelector<HTMLElement>('#players-stats')!;
  const searchEl = root.querySelector<HTMLInputElement>('#player-search')!;
  const fStatusEl = root.querySelector<HTMLSelectElement>('#players-f-status')!;
  const fIdentityEl = root.querySelector<HTMLSelectElement>('#players-f-identity')!;

  let rows: PlayerView[] = [];
  let total = 0;
  let page = 1;

  async function load(toPage = 1): Promise<void> {
    page = Math.max(1, toPage);
    tableEl.innerHTML = skeletonTable({ rows: PAGE_SIZE, columns: 7 });
    try {
      const res = await deps.client.listPlayers(deps.integratorId, {
        search: searchEl.value.trim() || undefined,
        status: (fStatusEl.value as StatusFilter) || undefined,
        identity: (fIdentityEl.value as IdentityFilter) || undefined,
        page,
        limit: PAGE_SIZE,
      });
      rows = res.players;
      total = res.total ?? res.players.length;
      renderStats(res.stats);
      renderResults();
    } catch (err) {
      statsEl.innerHTML = '';
      tableEl.innerHTML = `<p class="error">${
        err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err)
      }</p>`;
    }
  }

  function renderStats(
    stats?: { total: number; banned: number; anonymous: number; totalBalanceCredits: string },
  ): void {
    if (!stats) {
      statsEl.innerHTML = '';
      return;
    }
    const card = (label: string, value: string, color?: string): string =>
      `<div class="stat-card">
        <div class="stat-card-label">${label}</div>
        <div class="stat-card-value"${color ? ` style="color:${color}"` : ''}>${value}</div>
      </div>`;
    statsEl.innerHTML = `
      ${card('Total players', stats.total.toLocaleString())}
      ${card('Banned', stats.banned.toLocaleString(), stats.banned > 0 ? 'var(--warn)' : undefined)}
      ${card('Anonymous', stats.anonymous.toLocaleString())}
      ${card('Total balance', Number(stats.totalBalanceCredits).toLocaleString())}
    `;
  }

  function renderResults(): void {
    if (total === 0) {
      tableEl.innerHTML = emptyState({
        icon: 'search',
        title: 'No players match',
        hint: searchEl.value.trim()
          ? `Nothing found for "${searchEl.value.trim()}".`
          : 'No players match the current filters.',
      });
      return;
    }
    tableEl.innerHTML = `
      <div id="players-bulk-bar" class="row hidden players-bulk-bar">
        <span class="muted"><span id="players-bulk-count">0</span> selected</span>
        <button id="players-bulk-ban" class="primary">Ban selected</button>
        <button id="players-bulk-unban" class="ghost">Unban selected</button>
        <button id="players-bulk-clear" class="ghost" style="margin-left:auto">Clear</button>
      </div>
      ${renderTable(rows)}
      ${renderPager()}
    `;
    bindButtons();
  }

  function renderPager(): string {
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const from = (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(total, page * PAGE_SIZE);
    const btn = (label: string, target: number, opts: { active?: boolean; disabled?: boolean } = {}): string =>
      `<button class="pager-btn${opts.active ? ' active' : ''}" data-page="${target}"${opts.disabled ? ' disabled' : ''}>${label}</button>`;
    const nums = pageWindow(page, pageCount)
      .map((p) => (p === '…' ? '<span class="pager-gap">…</span>' : btn(String(p), p, { active: p === page })))
      .join('');
    return `<div class="pager">
      ${btn('‹ Prev', page - 1, { disabled: page <= 1 })}
      ${nums}
      ${btn('Next ›', page + 1, { disabled: page >= pageCount })}
      <span class="muted pager-info">${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}</span>
    </div>`;
  }

  function selectedIds(): string[] {
    return Array.from(tableEl.querySelectorAll<HTMLInputElement>('.players-row-check:checked')).map(
      (cb) => cb.dataset.id!,
    );
  }

  function refreshBulkBar(): void {
    const bar = tableEl.querySelector<HTMLElement>('#players-bulk-bar');
    if (!bar) return;
    const ids = selectedIds();
    bar.classList.toggle('hidden', ids.length === 0);
    const countEl = tableEl.querySelector<HTMLElement>('#players-bulk-count');
    if (countEl) countEl.textContent = String(ids.length);
  }

  async function bulkSetBanned(banned: boolean): Promise<void> {
    const ids = selectedIds();
    if (ids.length === 0) return;
    if (banned && !(await askConfirm(`Ban ${ids.length} player(s)?`))) return;
    const results = await Promise.allSettled(
      ids.map((id) => deps.client.updatePlayerSettings(id, { banned })),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed) showToast(`${ids.length - failed} ok, ${failed} failed.`, 'warn');
    else showToast(`${banned ? 'Banned' : 'Unbanned'} ${ids.length} player(s).`, 'success');
    await load(page);
  }

  function renderTable(players: PlayerView[]): string {
    const sort = getSortState('players');
    const sorted = applySort(players, sort, (p, key) => {
      switch (key) {
        case 'id':
          return p.id;
        case 'integratorPlayerRef':
          return p.integratorPlayerRef ?? '';
        case 'username':
          return p.username ?? '';
        case 'isAnonymous':
          return p.isAnonymous;
        case 'banned':
          return p.banned;
        case 'createdAt':
          return p.createdAt;
        case 'creditBalance':
          return toMajorNumber(p.creditBalance ?? '0', p.currency);
        default:
          return '';
      }
    });
    return `<table>
      <thead><tr>
        <th style="width:24px"><input type="checkbox" id="players-select-all" /></th>
        ${sortableTh('ID', 'id', sort)}
        ${sortableTh('Integrator ref', 'integratorPlayerRef', sort)}
        ${sortableTh('Username', 'username', sort)}
        ${sortableTh('Balance', 'creditBalance', sort)}
        ${sortableTh('Anonymous', 'isAnonymous', sort)}
        ${sortableTh('Banned', 'banned', sort)}
        ${sortableTh('Created', 'createdAt', sort)}
        <th style="text-align:right">Actions</th>
      </tr></thead>
      <tbody>${sorted
        .map(
          (p) => `<tr class="row-nav" data-row="${p.id}" data-row-href="/integrators/${encodeURIComponent(deps.integratorId)}/players/${p.id}">
            <td><input type="checkbox" class="players-row-check" data-id="${p.id}" /></td>
            <td><code style="color:var(--accent-2)">${p.id.slice(0, 8)}…</code></td>
            <td>${p.integratorPlayerRef ? `<code>${escapeHtml(p.integratorPlayerRef)}</code>` : '—'}</td>
            <td>${p.username ? escapeHtml(p.username) : '<span class="muted">(anon)</span>'}</td>
            <td style="font-variant-numeric:tabular-nums;text-align:right"><code>${fmtMoney(p.creditBalance ?? '0', p.currency)}</code> <span class="muted" style="font-size:11px">${escapeHtml(p.currency ?? '')}</span></td>
            <td><span class="pill ${p.isAnonymous ? '' : 'live'}">${p.isAnonymous ? 'yes' : 'no'}</span></td>
            <td><span class="pill ${p.banned ? 'warn' : ''}">${p.banned ? 'banned' : 'ok'}</span></td>
            <td><span class="muted">${fmtDate(p.createdAt)}</span></td>
            <td class="players-actions">
              <button class="icon-act" data-adjust="${p.id}" data-tip="Adjust balance" aria-label="Adjust balance">${icon('coin', { size: 15 })}</button>
              <button class="icon-act" data-toggle-anon="${p.id}" data-current="${p.isAnonymous}" data-tip="${p.isAnonymous ? 'Unmark anonymous' : 'Mark anonymous'}" aria-label="Toggle anonymity">${icon('user', { size: 15 })}</button>
              <button class="icon-act ${p.banned ? 'icon-act-active' : ''}" data-toggle-ban="${p.id}" data-current="${p.banned}" data-tip="${p.banned ? 'Unban' : 'Ban'}" aria-label="Ban">${icon('ban', { size: 15 })}</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
  }

  async function openAdjustModal(playerId: string): Promise<void> {
    // The amount is typed in the player's own currency ("50.00" USD) and sent
    // as the integer count of its minor unit the API takes (5000 cents).
    const cur = rows.find((r) => r.id === playerId)?.currency ?? '';
    if (!cur) {
      showToast('This player’s currency is not known — open the player to adjust the balance.', 'warn');
      return;
    }
    const result = await customModal<{ mode: string; amount: string; reason: string }>({
      title: 'Adjust balance',
      confirmLabel: 'Apply',
      body: html`
        <div class="modal-field">
          <label class="modal-field-label">Mode</label>
          <select id="adj-mode" class="modal-field-input">
            <option value="credit">Credit — call integrator</option>
            <option value="debit">Debit — call integrator</option>
            <option value="reconcile_manual">Manual reconcile — audit only</option>
          </select>
        </div>
        <div class="modal-field">
          <label class="modal-field-label">Amount (${moneyUnitHint(cur)})</label>
          <input id="adj-amount" type="text" inputmode="decimal" placeholder="amount in ${cur}" class="modal-field-input" />
        </div>
        <div class="modal-field">
          <label class="modal-field-label">Reason (audit-logged)</label>
          <input id="adj-reason" type="text" placeholder="Why is this adjustment being made?" class="modal-field-input" />
        </div>
        <p id="adj-err" class="error" style="margin:8px 0 0;font-size:12px;min-height:14px"></p>
      `,
      parseOnConfirm: (rootEl) => {
        const mode = rootEl.querySelector<HTMLSelectElement>('#adj-mode')!.value;
        const amount = readMoney(rootEl.querySelector<HTMLInputElement>('#adj-amount')!.value, cur);
        const reason = rootEl.querySelector<HTMLInputElement>('#adj-reason')!.value.trim();
        const errEl = rootEl.querySelector<HTMLElement>('#adj-err')!;
        if (!amount.ok) {
          errEl.textContent = amount.error;
          return null;
        }
        if (!reason) {
          errEl.textContent = 'A reason is required (it is audit-logged).';
          return null;
        }
        return { mode, amount: amount.minor.toString(), reason };
      },
    });
    if (!result) return;
    try {
      const res = await deps.client.adjustPlayerBalance(playerId, {
        mode: result.mode as 'credit' | 'debit' | 'reconcile_manual',
        creditAmount: result.amount,
        reason: result.reason,
      });
      const tail =
        res.mode === 'reconcile_manual'
          ? '(no integrator call)'
          : `New balance: ${fmtMoney(res.newBalanceCredits ?? '0', res.fiatCurrency || cur)} ${res.fiatCurrency || cur}`;
      showToast(`${res.mode}: ${tail}`, 'success');
      await load(page);
    } catch (err) {
      showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
    }
  }

  function bindButtons(): void {
    wireSortHeaders(tableEl, 'players', () => renderResults());
    tableEl.querySelectorAll<HTMLInputElement>('.players-row-check').forEach((cb) => {
      cb.addEventListener('change', refreshBulkBar);
    });
    tableEl.querySelector<HTMLInputElement>('#players-select-all')?.addEventListener('change', (ev) => {
      const checked = (ev.target as HTMLInputElement).checked;
      tableEl.querySelectorAll<HTMLInputElement>('.players-row-check').forEach((cb) => {
        cb.checked = checked;
      });
      refreshBulkBar();
    });
    tableEl.querySelector<HTMLButtonElement>('#players-bulk-ban')?.addEventListener('click', () => void bulkSetBanned(true));
    tableEl.querySelector<HTMLButtonElement>('#players-bulk-unban')?.addEventListener('click', () => void bulkSetBanned(false));
    tableEl.querySelector<HTMLButtonElement>('#players-bulk-clear')?.addEventListener('click', () => {
      tableEl.querySelectorAll<HTMLInputElement>('.players-row-check').forEach((cb) => (cb.checked = false));
      const selectAll = tableEl.querySelector<HTMLInputElement>('#players-select-all');
      if (selectAll) selectAll.checked = false;
      refreshBulkBar();
    });
    tableEl.querySelectorAll<HTMLButtonElement>('.pager-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = Number(btn.dataset.page);
        if (Number.isFinite(target) && target !== page) void load(target);
      });
    });
    tableEl.querySelectorAll<HTMLButtonElement>('button[data-toggle-anon]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.toggleAnon!;
        const current = btn.dataset.current === 'true';
        try {
          await deps.client.updatePlayerSettings(id, { isAnonymous: !current });
          await load(page);
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    tableEl.querySelectorAll<HTMLButtonElement>('button[data-adjust]').forEach((btn) => {
      btn.addEventListener('click', () => void openAdjustModal(btn.dataset.adjust!));
    });
    tableEl.querySelectorAll<HTMLButtonElement>('button[data-toggle-ban]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.toggleBan!;
        const current = btn.dataset.current === 'true';
        if (!current && !(await askConfirm('Ban this player?'))) return;
        try {
          await deps.client.updatePlayerSettings(id, { banned: !current });
          await load(page);
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }

  root.querySelector<HTMLButtonElement>('#player-search-run')!.addEventListener('click', () => void load(1));
  root.querySelector<HTMLButtonElement>('#player-search-clear')!.addEventListener('click', () => {
    searchEl.value = '';
    fStatusEl.value = '';
    fIdentityEl.value = '';
    void load(1);
  });
  [fStatusEl, fIdentityEl].forEach((el) => el.addEventListener('change', () => void load(1)));
  root.querySelector<HTMLButtonElement>('#player-export')!.addEventListener('click', () => {
    if (rows.length === 0) {
      showToast('No players to export.', 'warn');
      return;
    }
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    exportXlsx(
      `apx-players-p${page}-${stamp}.xlsx`,
      rows.map((p) => ({
        id: p.id,
        integratorPlayerRef: p.integratorPlayerRef ?? '',
        username: p.username ?? '',
        // The balance as a decimal in the player's currency ("1234.56" in USD).
        currency: p.currency ?? '',
        creditBalance: p.currency ? toDecimalString(p.creditBalance ?? '0', p.currency) : (p.creditBalance ?? '0'),
        isAnonymous: p.isAnonymous,
        banned: p.banned,
        createdAt: p.createdAt,
      })),
    );
    showToast(`Exported page ${page} (${rows.length} players).`, 'success');
  });
  searchEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void load(1);
  });

  await load(1);
}

/** Windowed page numbers with ellipses: 1 … 4 5 [6] 7 8 … 20. */
function pageWindow(current: number, count: number): Array<number | '…'> {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const out: Array<number | '…'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(count - 1, current + 1);
  if (start > 2) out.push('…');
  for (let i = start; i <= end; i++) out.push(i);
  if (end < count - 1) out.push('…');
  out.push(count);
  return out;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
